import { useAppSelector } from "@/store/store-hooks";
import { selectCanUploadWithDataManagement } from "@/store/subscription-features/subscription-features-selectors";
import {
  selectCurrentUser,
  selectHasWritePermission,
} from "@/store/user-selectors";
import { FaroButton, FaroButtonProps, FaroTooltip } from "@faro-lotv/flat-ui";
import { selectCompanyId, selectProjectId } from "@faro-lotv/project-source";
import type { JSX } from "react";
import { useIsDataPreparationReadOnly } from "../hooks/use-is-data-preparation-read-only";
import { selectIsSavingDraftChanges } from "../store/data-preparation-ui/draft-saving-selectors";
import { selectIsAnyEditModeEnabled } from "../store/user-edits/data-preparation-user-edit-selectors";
import { useDraftImportDataDialogStore } from "./draft-import-data-dialog";

type ImportCapturesButtonProps = Pick<FaroButtonProps, "sx">;

/**
 * @returns button to open the simplified import data dialog of the Draft View.
 * The dialog itself is rendered by DataPreparationUi so that it survives an unmount of the sidebar.
 */
export function ImportCapturesButton({
  sx,
}: ImportCapturesButtonProps): JSX.Element | null {
  const hasWritePermission = useAppSelector(selectHasWritePermission);
  const isReadOnly = useIsDataPreparationReadOnly();
  const canUpload = useAppSelector(selectCanUploadWithDataManagement);
  const isEditing = useAppSelector(selectIsAnyEditModeEnabled);
  const isSaving = useAppSelector(selectIsSavingDraftChanges);
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = useAppSelector(selectCompanyId);
  const projectId = useAppSelector(selectProjectId);

  const setIsDialogOpen = useDraftImportDataDialogStore(
    (state) => state.setIsOpen,
  );

  if (
    isReadOnly ||
    !hasWritePermission ||
    !canUpload ||
    !currentUser ||
    !companyId ||
    !projectId
  ) {
    return null;
  }

  let disabledMessage: string | undefined;
  if (isEditing) {
    disabledMessage =
      "Importing captures is disabled while editing the registration. Please save your changes and try again.";
  } else if (isSaving) {
    disabledMessage =
      "Importing captures is disabled while changes are being saved. Please wait until saving has completed.";
  }

  return (
    <FaroTooltip title={disabledMessage} sx={{ display: "inline-grid" }}>
      <FaroButton
        variant="primary"
        aria-label="open import data dialog"
        onClick={() => setIsDialogOpen(true)}
        sx={sx}
        disabled={isEditing || isSaving}
      >
        Import captures
      </FaroButton>
    </FaroTooltip>
  );
}
